import { useEffect, useState } from "react";
import client from "@/lib/api";
import { useProject } from "@/components/AppShell";
import { DownloadMdButton } from "@/components/DownloadMdButton";
import { buildKeywordsMd } from "@/lib/mdBuilders";
import { toast } from "sonner";
import { Plus, Trash2, Sparkles, ArrowUpDown, Lock, Wand2, FileText, Upload, RefreshCw, Compass, Quote, Loader2, Megaphone, Network, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import { DataSourceBadge } from "@/components/DataSourceBadge";
import { RankDropExplainer } from "@/components/RankDropExplainer";
import { ContentBrief } from "@/components/ContentBrief";
import { BatchImportModal, KEYWORD_CREDIT_COST } from "@/components/BatchImportModal";
import { KeywordResearchModal } from "@/components/KeywordResearchModal";
import { CreditActionButton } from "@/components/CreditActionButton";
import { MetricInfo } from "@/components/MetricInfo";

const COLUMN_METRIC = {
    position: "keyword_position",
    change: "keyword_change",
    volume: "search_volume",
    difficulty: "keyword_difficulty",
    opportunity_score: "keyword_opportunity_score",
    cpc: "cpc",
    intent: "search_intent",
};

function PpcResearchSection({ rows }) {
    return (
        <div className="border border-border bg-card/40 overflow-x-auto" data-testid="ppc-research-table">
            <div className="p-4 border-b border-border">
                <div className="overline">PPC Research</div>
                <p className="text-xs text-muted-foreground mt-1">Live ad competitors and top-of-page bid ranges — bundled free with your SERP + volume refresh, no extra credits.</p>
            </div>
            <table className="w-full text-sm">
                <thead className="text-xs text-muted-foreground">
                    <tr className="border-b border-border">
                        <th className="text-left p-3">Keyword</th>
                        <th className="text-right p-3">Volume</th>
                        <th className="text-left p-3">Ad competition</th>
                        <th className="text-right p-3">Top-of-page bid</th>
                        <th className="text-left p-3">Live ad competitors</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((r) => {
                        const live = r.metrics_available !== false;
                        const hasBid = live && (r.low_top_of_page_bid || r.high_top_of_page_bid);
                        return (
                            <tr key={r.id} className="border-b border-border/50 hover:bg-accent/20" data-testid={`ppc-row-${r.id}`}>
                                <td className="p-3 font-mono">{r.keyword}</td>
                                <td className="p-3 text-right font-mono text-muted-foreground">{live ? (r.volume || 0).toLocaleString() : "—"}</td>
                                <td className="p-3">
                                    {live && r.ads_competition ? (
                                        <span className={`inline-block px-1.5 py-0.5 text-xs border ${r.ads_competition === "HIGH" ? "border-destructive/50 text-destructive" : r.ads_competition === "MEDIUM" ? "border-warning/50 text-warning" : "border-success/40 text-success"}`}>
                                            {r.ads_competition}
                                        </span>
                                    ) : <span className="text-xs text-muted-foreground">—</span>}
                                </td>
                                <td className="p-3 text-right font-mono text-muted-foreground">
                                    {hasBid ? `$${r.low_top_of_page_bid.toFixed(2)} – $${r.high_top_of_page_bid.toFixed(2)}` : "—"}
                                </td>
                                <td className="p-3">
                                    {live && r.paid_ads?.length ? (
                                        <div className="flex gap-1 flex-wrap" data-testid={`ppc-ads-${r.id}`}>
                                            {r.paid_ads.slice(0, 3).map((ad, i) => (
                                                <span key={i} title={ad.title || ""} className="text-xs border border-border px-1.5 py-0.5 text-muted-foreground">{ad.domain || "?"}</span>
                                            ))}
                                        </div>
                                    ) : <span className="text-xs text-muted-foreground/70">No live ads detected</span>}
                                </td>
                            </tr>
                        );
                    })}
                    {rows.length === 0 && (
                        <tr><td colSpan={5} className="p-6 text-center text-muted-foreground text-sm">No keywords tracked yet.</td></tr>
                    )}
                </tbody>
            </table>
        </div>
    );
}

function ClustersSection({ projectId }) {
    const [minShared, setMinShared] = useState(3);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    const compute = async (ms) => {
        setLoading(true);
        try {
            const r = await client.get(`/projects/${projectId}/keywords/clusters`, { params: { min_shared: ms } });
            setData(r.data);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't compute clusters");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (projectId) compute(minShared);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectId]);

    const applyMinShared = () => compute(minShared);

    return (
        <div className="border border-border bg-card/40" data-testid="clusters-section">
            <div className="p-4 border-b border-border flex items-center justify-between flex-wrap gap-3">
                <div>
                    <div className="overline inline-flex items-center gap-2"><Network className="w-3.5 h-3.5" /> Keyword clusters</div>
                    <div className="text-xs text-muted-foreground mt-1">Groups of tracked keywords whose top-10 Google results overlap — optimise one page for all of them.</div>
                </div>
                <div className="flex items-center gap-2">
                    <label className="text-[10px] font-mono text-muted-foreground">Min shared URLs</label>
                    <input
                        data-testid="clusters-min-shared-input"
                        type="number"
                        min={1}
                        max={10}
                        value={minShared}
                        onChange={(e) => setMinShared(Math.max(1, Math.min(10, Number(e.target.value) || 3)))}
                        className="w-16 bg-input border border-border px-2 py-1.5 text-sm font-mono focus:outline-none focus:border-primary"
                    />
                    <button
                        data-testid="clusters-recompute-btn"
                        onClick={applyMinShared}
                        disabled={loading}
                        className="border border-border px-3 py-1.5 text-xs font-mono hover:border-primary hover:text-primary disabled:opacity-50 inline-flex items-center gap-1.5"
                    >
                        {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                        Recompute
                    </button>
                </div>
            </div>

            {loading && !data && (
                <div className="p-6 text-center text-sm text-muted-foreground">Clustering…</div>
            )}

            {data && (
                <div className="p-4 space-y-4" data-testid="clusters-result">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-xs font-mono">
                        <div><div className="text-muted-foreground">Clusters</div><div className="text-foreground text-lg">{data.clusters.length}</div></div>
                        <div><div className="text-muted-foreground">Unclustered</div><div className="text-foreground text-lg">{data.unclustered.length}</div></div>
                        <div><div className="text-muted-foreground">Missing SERP</div><div className="text-warning text-lg">{data.missing_serp.length}</div></div>
                        <div><div className="text-muted-foreground">Tracked total</div><div className="text-foreground text-lg">{data.total_keywords}</div></div>
                    </div>

                    {data.missing_serp.length > 0 && (
                        <div className="border border-warning/40 bg-warning/5 p-3 text-xs" data-testid="clusters-missing-notice">
                            <div className="text-warning font-mono mb-1">{data.missing_serp.length} keyword{data.missing_serp.length === 1 ? "" : "s"} have no cached SERP yet.</div>
                            <div className="text-muted-foreground">Run "Pull all live data" or "Refresh SERP" first to include them in clustering.</div>
                        </div>
                    )}

                    {data.clusters.length === 0 && data.missing_serp.length < data.total_keywords && (
                        <div className="border border-border/60 p-6 text-center text-sm text-muted-foreground" data-testid="clusters-empty">
                            No clusters at {minShared}+ shared URLs. Try lowering the threshold.
                        </div>
                    )}

                    {data.clusters.map((c, idx) => (
                        <div key={c.id} className="border border-border/60 p-4" data-testid="cluster-row">
                            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                                <div className="flex items-center gap-2">
                                    <span className="text-[10px] font-mono bg-primary/15 text-primary px-2 py-0.5">CLUSTER #{idx + 1}</span>
                                    <span className="text-sm font-medium">{c.size} keywords share SERPs</span>
                                </div>
                                <span className="text-[10px] font-mono text-muted-foreground">{c.shared_urls.length} overlapping URL{c.shared_urls.length === 1 ? "" : "s"}</span>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <div className="text-[10px] font-mono text-muted-foreground mb-2 uppercase tracking-wider">Keywords in cluster</div>
                                    <div className="space-y-1" data-testid="cluster-keywords">
                                        {c.keywords.map((k) => (
                                            <div key={k.id} className="flex items-center justify-between text-xs font-mono">
                                                <span className="truncate">{k.keyword}</span>
                                                {k.position != null ? (
                                                    <span className={k.position <= 3 ? "text-success" : k.position <= 10 ? "text-primary" : "text-muted-foreground"}>#{k.position}</span>
                                                ) : (
                                                    <span className="text-muted-foreground">—</span>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                <div>
                                    <div className="text-[10px] font-mono text-muted-foreground mb-2 uppercase tracking-wider">Pages ranking for multiple</div>
                                    <div className="space-y-1" data-testid="cluster-shared-urls">
                                        {c.shared_urls.map((s) => (
                                            <a key={s.url} href={s.url} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-xs font-mono text-muted-foreground hover:text-primary truncate">
                                                <span className="text-primary">{s.count}×</span>
                                                <span className="truncate">{s.url.replace(/^https?:\/\//, "")}</span>
                                                <ExternalLink className="w-3 h-3 shrink-0" />
                                            </a>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default function Keywords() {
    const project = useProject();
    const [rows, setRows] = useState([]);
    const [meta, setMeta] = useState({ tier: "free", limit: 3, history_unlocked: false, source: "mock" });
    const [newKw, setNewKw] = useState("");
    const [q, setQ] = useState("");
    const [loading, setLoading] = useState(true);
    const [sort, setSort] = useState({ key: "position", dir: "asc" });
    const [explainer, setExplainer] = useState({ open: false, id: null, label: "" });
    const [brief, setBrief] = useState({ open: false, keyword: "" });
    const [batchOpen, setBatchOpen] = useState(false);
    const [researchOpen, setResearchOpen] = useState(false);
    const [checkingAi, setCheckingAi] = useState(null);
    const [aiSelected, setAiSelected] = useState(new Set());
    const [bulkAiJob, setBulkAiJob] = useState(null);
    const [view, setView] = useState("rankings"); // "rankings" | "ppc" | "clusters"

    const load = () => {
        if (!project) return;
        setLoading(true);
        client.get(`/projects/${project.id}/keywords`).then((r) => {
            const payload = r.data;
            // Support both new shape {keywords, tier, limit, history_unlocked} and legacy array
            if (Array.isArray(payload)) {
                setRows(payload);
                setMeta({ tier: "free", limit: 3, history_unlocked: false, source: "mock" });
            } else {
                setRows(payload.keywords || []);
                setMeta({ tier: payload.tier, limit: payload.limit, history_unlocked: payload.history_unlocked, source: payload.source || "mock" });
            }
        }).finally(() => setLoading(false));
    };
    useEffect(load, [project]);

    const add = async (e) => {
        e.preventDefault();
        if (!newKw.trim()) return;
        try {
            const r = await client.post(`/projects/${project.id}/keywords`, { keyword: newKw, project_id: project.id });
            setRows((prev) => [...prev, r.data]);
            setNewKw("");
            toast.success(`Tracking "${r.data.keyword}"`);
        } catch (err) {
            if (err?.response?.status === 402) {
                toast.error(err.response.data.detail || "Plan limit reached");
            } else {
                toast.error("Failed to add");
            }
        }
    };

    const remove = async (id) => {
        await client.delete(`/keywords/${id}`);
        setRows((prev) => prev.filter((r) => r.id !== id));
    };

    const checkAiCitation = async (id) => {
        setCheckingAi(id);
        try {
            const r = await client.post(`/projects/${project.id}/keywords/${id}/check-ai-citation`);
            setRows((prev) => prev.map((row) => (row.id === id ? { ...row, ...r.data } : row)));
            if (r.data.ai_overview_cited) {
                toast.success(`Cited! Google's AI Overview quotes you for "${r.data.keyword}"`);
            } else if (r.data.ai_overview) {
                toast(`AI Overview exists for "${r.data.keyword}" but doesn't cite you yet`);
            } else {
                toast(`No AI Overview detected for "${r.data.keyword}"`);
            }
        } catch (err) {
            toast.error(err?.response?.data?.detail || "AI citation check failed");
        } finally {
            setCheckingAi(null);
        }
    };

    const toggleAiSelect = (id) => setAiSelected((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
    });
    const startBulkAiCheck = async () => {
        const ids = Array.from(aiSelected);
        try {
            const r = await client.post(`/projects/${project.id}/keywords/bulk-check-ai-citation`, { keyword_ids: ids });
            setBulkAiJob({ jobId: r.data.job_id, total: r.data.total, completed: 0, cited: 0, status: "running" });
            setAiSelected(new Set());
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not start bulk AI citation check");
        }
    };

    useEffect(() => {
        if (!bulkAiJob || bulkAiJob.status === "done" || !project) return;
        const iv = setInterval(async () => {
            try {
                const r = await client.get(`/projects/${project.id}/keywords/bulk-check-ai-citation/${bulkAiJob.jobId}`);
                const job = r.data;
                setBulkAiJob({ jobId: job.id, total: job.total, completed: job.completed, cited: job.cited, status: job.status });
                if (job.status === "done") {
                    const byId = new Map((job.results || []).filter((x) => x.ok).map((x) => [x.keyword_id, x]));
                    setRows((prev) => prev.map((row) => {
                        const res = byId.get(row.id);
                        if (!res) return row;
                        return { ...row, ai_overview: res.ai_overview, ai_overview_cited: res.ai_overview_cited, ai_overview_citation: res.ai_overview_citation, ai_overview_checked_at: res.ai_overview_checked_at };
                    }));
                    toast.success(`Checked ${job.total} keyword${job.total === 1 ? "" : "s"} · ${job.cited} cited in Google's AI Overview`);
                }
            } catch (err) { /* transient — keep polling */ }
        }, 2500);
        return () => clearInterval(iv);
    }, [bulkAiJob?.jobId, bulkAiJob?.status, project]);

    const refreshSerp = async () => {
        try {
            const r = await client.post(`/projects/${project.id}/keywords/refresh-serp`);
            const payload = r.data;
            setRows(payload.keywords || []);
            setMeta({
                tier: payload.tier,
                limit: payload.limit,
                history_unlocked: payload.history_unlocked,
                source: payload.source || "mock",
            });
            toast.success(`Refreshed ${payload.refreshed} keywords · ${payload.cost} credits`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Refresh failed");
        }
    };

    const filtered = rows.filter((r) => !q || r.keyword.toLowerCase().includes(q.toLowerCase()));
    filtered.sort((a, b) => {
        const va = a[sort.key], vb = b[sort.key];
        if (typeof va === "string") return sort.dir === "asc" ? va.localeCompare(vb) : vb.localeCompare(va);
        return sort.dir === "asc" ? va - vb : vb - va;
    });

    const allAiSelected = filtered.length > 0 && filtered.every((r) => aiSelected.has(r.id));
    const toggleAiSelectAll = () => setAiSelected(allAiSelected ? new Set() : new Set(filtered.map((r) => r.id)));

    const toggleSort = (key) => setSort((s) => s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" });

    if (!project) return null;

    return (
        <div className="space-y-6" data-testid="keywords-page">
            <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="overline">Rank tracker</div>
                    <div className="flex items-center gap-3 mt-2">
                        <h1 className="font-display text-3xl md:text-4xl tracking-tight">Keywords</h1>
                        <DataSourceBadge source={meta.source} />
                    </div>
                    <p className="text-sm text-muted-foreground mt-2">Position, SERP features, AI Overviews and search intent — updated daily.</p>
                </div>
                <div className="flex flex-col items-end gap-2">
                    <div className="text-xs font-mono text-muted-foreground">
                        <span className="text-foreground">{rows.length}</span>/{meta.limit} keywords ·
                        <span className="ml-1 uppercase tracking-wider text-primary">{meta.tier}</span>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 justify-end">
                        <form onSubmit={add} className="flex gap-2" data-testid="add-keyword-form">
                            <input
                                data-testid="new-keyword-input"
                                value={newKw} onChange={(e) => setNewKw(e.target.value)}
                                placeholder={rows.length >= meta.limit ? "Limit reached — upgrade plan" : "Add keyword…"}
                                disabled={rows.length >= meta.limit}
                                className="bg-input border border-border px-3 py-2 text-sm font-mono focus:outline-none focus:border-primary min-w-[240px] disabled:opacity-50"
                            />
                            <button
                                data-testid="add-keyword-btn"
                                disabled={rows.length >= meta.limit}
                                title={`Costs ${KEYWORD_CREDIT_COST} credit — covers the SERP + volume lookup on next dashboard load.`}
                                className="bg-primary text-primary-foreground px-4 py-2 text-sm font-medium hover:bg-primary/90 flex items-center gap-2 disabled:opacity-50"
                            >
                                <Plus className="w-4 h-4" /> Add
                                <span
                                    data-testid="add-keyword-cost-badge"
                                    className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider bg-primary-foreground/15 border border-primary-foreground/30 px-1.5 py-0.5"
                                >
                                    +{KEYWORD_CREDIT_COST} cr
                                </span>
                            </button>
                        </form>
                        <DownloadMdButton
                            filenamePrefix={`inphase-keywords-${project?.domain || "project"}`}
                            buildMarkdown={() => buildKeywordsMd({ project, rows })}
                            disabled={!rows.length}
                            testId="download-keywords-md"
                        />
                        <button
                            data-testid="open-research-btn"
                            onClick={() => setResearchOpen(true)}
                            disabled={rows.length >= meta.limit}
                            title="Search a seed keyword/topic for new keyword ideas"
                            className="border border-border px-3 py-2 text-xs font-mono hover:border-primary hover:text-primary flex items-center gap-2 disabled:opacity-50"
                        >
                            <Compass className="w-3.5 h-3.5" /> Research keywords
                        </button>
                        <button
                            data-testid="open-batch-import-btn"
                            onClick={() => setBatchOpen(true)}
                            disabled={rows.length >= meta.limit}
                            title="Paste up to 50 keywords at once"
                            className="border border-border px-3 py-2 text-xs font-mono hover:border-primary hover:text-primary flex items-center gap-2 disabled:opacity-50"
                        >
                            <Upload className="w-3.5 h-3.5" /> Batch import
                        </button>
                        {rows.length > 0 && (
                            <CreditActionButton
                                actionKey="keyword_add"
                                costMultiplier={rows.length}
                                testid="refresh-serp-btn"
                                variant="outline"
                                onConfirm={refreshSerp}
                                description={`Pulls fresh SERP + volume data from DataForSEO for every one of your ${rows.length} tracked keyword${rows.length === 1 ? "" : "s"}. Cache is reused within a 24h window at no cost.`}
                            >
                                <span className="inline-flex items-center gap-1">
                                    <RefreshCw className="w-3.5 h-3.5" /> Refresh SERP
                                </span>
                            </CreditActionButton>
                        )}
                    </div>
                    <div className="text-[10px] font-mono text-muted-foreground/70">
                        Each tracked keyword uses 1 credit on refresh · Admin tiers exempt
                    </div>
                </div>
            </div>

            <div className="flex items-center gap-1 border border-border w-fit p-0.5" role="tablist" data-testid="keywords-view-tabs">
                <button
                    data-testid="view-tab-rankings"
                    role="tab"
                    aria-selected={view === "rankings"}
                    onClick={() => setView("rankings")}
                    className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wider ${view === "rankings" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
                >
                    Rankings
                </button>
                <button
                    data-testid="view-tab-ppc"
                    role="tab"
                    aria-selected={view === "ppc"}
                    onClick={() => setView("ppc")}
                    className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wider inline-flex items-center gap-1.5 ${view === "ppc" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
                >
                    <Megaphone className="w-3 h-3" /> PPC Research
                </button>
                <button
                    data-testid="view-tab-clusters"
                    role="tab"
                    aria-selected={view === "clusters"}
                    onClick={() => setView("clusters")}
                    className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wider inline-flex items-center gap-1.5 ${view === "clusters" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
                >
                    <Network className="w-3 h-3" /> Clusters
                </button>
            </div>

            {view === "ppc" && <PpcResearchSection rows={filtered} />}
            {view === "clusters" && <ClustersSection projectId={project?.id} />}

            {view === "rankings" && !meta.history_unlocked && (
                <div className="border border-primary/60 bg-primary/5 p-4 flex items-center justify-between gap-3" data-testid="history-upsell">
                    <div className="flex items-center gap-3">
                        <Lock className="w-4 h-4 text-primary shrink-0" />
                        <div className="text-sm">
                            Historical rank trends & daily snapshots are an <span className="text-primary">Agency</span> feature.
                            You're seeing current positions only.
                        </div>
                    </div>
                    <Link to="/pricing" data-testid="keyword-upgrade-link" className="text-xs text-primary hover:underline whitespace-nowrap">Upgrade →</Link>
                </div>
            )}

            <div className="flex items-center gap-4">
                <input
                    data-testid="keyword-search"
                    value={q} onChange={(e) => setQ(e.target.value)}
                    placeholder="Filter keywords…"
                    className="bg-input border border-border px-3 py-2 text-sm font-mono focus:outline-none focus:border-primary flex-1 max-w-md"
                />
                <div className="text-xs font-mono text-muted-foreground">{filtered.length} of {rows.length}</div>
            </div>

            {view === "rankings" && (aiSelected.size > 0 || bulkAiJob) && (
                <div className="flex items-center justify-between gap-4 border border-primary/50 bg-primary/5 px-4 py-2" data-testid="bulk-ai-citation-bar">
                    {bulkAiJob ? (
                        <div className="text-xs font-mono text-muted-foreground flex items-center gap-2">
                            {bulkAiJob.status === "done" ? (
                                <span className="text-success" data-testid="bulk-ai-job-done">Done — {bulkAiJob.completed}/{bulkAiJob.total} checked, {bulkAiJob.cited} cited</span>
                            ) : (
                                <span data-testid="bulk-ai-job-progress" className="inline-flex items-center gap-2">
                                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                    Checking AI citations: {bulkAiJob.completed} of {bulkAiJob.total} ({bulkAiJob.cited} cited so far)
                                </span>
                            )}
                        </div>
                    ) : (
                        <div className="text-xs font-mono text-muted-foreground">{aiSelected.size} keyword{aiSelected.size === 1 ? "" : "s"} selected</div>
                    )}
                    {!bulkAiJob && aiSelected.size > 0 && (
                        <div className="flex items-center gap-2">
                            <button data-testid="clear-ai-selection-btn" onClick={() => setAiSelected(new Set())} className="text-xs text-muted-foreground hover:text-foreground">Clear</button>
                            <CreditActionButton
                                actionKey="ai_citation_check"
                                costMultiplier={aiSelected.size}
                                testid="bulk-check-ai-citation-btn"
                                onConfirm={startBulkAiCheck}
                                description={`Queues a real DataForSEO AI Overview citation check for ${aiSelected.size} keyword${aiSelected.size === 1 ? "" : "s"}. Runs in the background (~${Math.ceil(aiSelected.size / 3) * 10}s) — you can keep using the app while it finishes.`}
                            >
                                <span className="inline-flex items-center gap-1"><Quote className="w-3.5 h-3.5" /> Check AI Citations</span>
                            </CreditActionButton>
                        </div>
                    )}
                    {bulkAiJob?.status === "done" && (
                        <button data-testid="dismiss-bulk-ai-job-btn" onClick={() => setBulkAiJob(null)} className="text-xs text-muted-foreground hover:text-foreground">Dismiss</button>
                    )}
                </div>
            )}

            <div className="border border-border bg-card/40 overflow-x-auto" style={view === "rankings" ? {} : { display: "none" }}>
                <table className="w-full text-sm">
                    <thead className="text-xs text-muted-foreground">
                        <tr className="border-b border-border">
                            <th className="p-3 w-8">
                                <input
                                    type="checkbox"
                                    checked={allAiSelected}
                                    onChange={toggleAiSelectAll}
                                    data-testid="select-all-ai-checkbox"
                                    title="Select all visible keywords for a bulk AI citation check"
                                />
                            </th>
                            {[
                                ["keyword", "Keyword"],
                                ["position", "Pos"],
                                ["change", "Δ"],
                                ["volume", "Volume"],
                                ["difficulty", "KD"],
                                ["opportunity_score", "Opp."],
                                ["cpc", "CPC"],
                                ["intent", "Intent"],
                            ].map(([k, l]) => (
                                <th key={k} className="text-left p-3 cursor-pointer hover:text-foreground" onClick={() => toggleSort(k)}>
                                    <span className="inline-flex items-center gap-1">
                                        {l}
                                        {COLUMN_METRIC[k] && <MetricInfo metricKey={COLUMN_METRIC[k]} />}
                                        <ArrowUpDown className="w-3 h-3 opacity-40" />
                                    </span>
                                </th>
                            ))}
                            <th className="text-left p-3">
                                <span className="inline-flex items-center gap-1">
                                    SERP <MetricInfo metricKey="serp_ai_overview" /> / <MetricInfo metricKey="ai_overview_citation" />
                                </span>
                            </th>
                            <th className="p-3"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={11} className="p-6 text-center text-muted-foreground text-sm">loading…</td></tr>
                        ) : filtered.map((r) => {
                            const live = r.metrics_available !== false;
                            return (
                            <tr key={r.id} className="border-b border-border/50 hover:bg-accent/20">
                                <td className="p-3">
                                    <input
                                        type="checkbox"
                                        checked={aiSelected.has(r.id)}
                                        onChange={() => toggleAiSelect(r.id)}
                                        data-testid={`ai-select-checkbox-${r.id}`}
                                    />
                                </td>
                                <td className="p-3 font-mono">{r.keyword}</td>
                                <td className="p-3 font-mono">{live ? (r.position ?? "not ranked") : "—"}</td>
                                <td className={`p-3 font-mono ${!live ? "text-muted-foreground" : r.change > 0 ? "text-success" : r.change < 0 ? "text-destructive" : "text-muted-foreground"}`}>
                                    {live ? <>{r.change > 0 ? "+" : ""}{r.change ?? 0}</> : "—"}
                                </td>
                                <td className="p-3 font-mono text-muted-foreground">{live ? (r.volume || 0).toLocaleString() : "—"}</td>
                                <td className="p-3 font-mono">
                                    {live ? (
                                        <span className={`inline-block px-1.5 py-0.5 text-xs border ${r.difficulty > 70 ? "border-destructive/50 text-destructive" : r.difficulty > 40 ? "border-warning/50 text-warning" : "border-success/40 text-success"}`}>
                                            {r.difficulty ?? 0}
                                        </span>
                                    ) : (
                                        <span className="inline-block px-1.5 py-0.5 text-xs border border-border text-muted-foreground">—</span>
                                    )}
                                </td>
                                <td className="p-3 font-mono">
                                    {live ? (
                                        <span className={`inline-block px-1.5 py-0.5 text-xs border ${r.opportunity_score >= 70 ? "border-success/40 text-success" : r.opportunity_score >= 40 ? "border-warning/50 text-warning" : "border-destructive/50 text-destructive"}`}>
                                            {r.opportunity_score ?? 0}
                                        </span>
                                    ) : (
                                        <span className="inline-block px-1.5 py-0.5 text-xs border border-border text-muted-foreground">—</span>
                                    )}
                                </td>
                                <td className="p-3 font-mono text-muted-foreground">{live ? `$${r.cpc ?? 0}` : "—"}</td>
                                <td className="p-3 text-xs text-muted-foreground">{r.intent || "—"}</td>
                                <td className="p-3">
                                    {live ? (
                                        <div className="flex gap-1 flex-wrap items-center">
                                            {r.ai_overview_cited ? (
                                                <span
                                                    data-testid={`ai-cited-badge-${r.id}`}
                                                    title={r.ai_overview_citation?.url ? `Cited: ${r.ai_overview_citation.url}` : "Cited in Google's AI Overview"}
                                                    className="inline-flex items-center gap-1 text-xs bg-success/10 text-success px-1.5 py-0.5 border border-success/40"
                                                >
                                                    <Sparkles className="w-3 h-3" /> AI Cited
                                                </span>
                                            ) : r.ai_overview && (
                                                <span className="inline-flex items-center gap-1 text-xs bg-secondary/10 text-secondary px-1.5 py-0.5 border border-secondary/40">
                                                    <Sparkles className="w-3 h-3" /> AIO
                                                </span>
                                            )}
                                            {(r.serp_features || []).slice(0, 2).map((f) => (
                                                <span key={f} className="text-xs text-muted-foreground border border-border px-1.5 py-0.5">{f}</span>
                                            ))}
                                            <button
                                                data-testid={`check-ai-citation-btn-${r.id}`}
                                                onClick={() => checkAiCitation(r.id)}
                                                disabled={checkingAi === r.id}
                                                title="Real-time check: does Google's AI Overview cite you for this keyword? (~10s, 1 credit)"
                                                className="inline-flex items-center gap-1 text-[10px] text-muted-foreground hover:text-primary underline disabled:opacity-50"
                                            >
                                                <Quote className="w-2.5 h-2.5" />
                                                {checkingAi === r.id ? "Checking…" : r.ai_overview_checked_at ? "Recheck AI" : "Check AI"}
                                            </button>
                                        </div>
                                    ) : (
                                        <span className="text-xs text-muted-foreground/70">Refresh SERP for live data</span>
                                    )}
                                </td>
                                <td className="p-3 text-right">
                                    <div className="flex items-center gap-2 justify-end">
                                        <button
                                            data-testid={`brief-keyword-${r.id}`}
                                            onClick={() => setBrief({ open: true, keyword: r.keyword })}
                                            title="Generate SERP-grounded content brief (5 credits)"
                                            className="text-muted-foreground hover:text-primary p-1"
                                        >
                                            <FileText className="w-4 h-4" />
                                        </button>
                                        <button
                                            data-testid={`explain-keyword-${r.id}`}
                                            onClick={() => setExplainer({ open: true, id: r.id, label: r.keyword })}
                                            title="Ask Claude why this keyword moved (2 credits)"
                                            className="text-muted-foreground hover:text-primary p-1"
                                        >
                                            <Wand2 className="w-4 h-4" />
                                        </button>
                                        <button data-testid={`delete-keyword-${r.id}`} onClick={() => remove(r.id)} className="text-muted-foreground hover:text-destructive p-1">
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            <RankDropExplainer
                open={explainer.open}
                onOpenChange={(v) => setExplainer((s) => ({ ...s, open: v }))}
                projectId={project.id}
                keywordId={explainer.id}
                keywordLabel={explainer.label}
            />
            <ContentBrief
                open={brief.open}
                onOpenChange={(v) => setBrief((s) => ({ ...s, open: v }))}
                projectId={project.id}
                initialKeyword={brief.keyword}
            />

            <BatchImportModal
                open={batchOpen}
                onClose={() => setBatchOpen(false)}
                projectId={project.id}
                existingKeywords={rows.map((r) => r.keyword)}
                limit={meta.limit}
                tier={meta.tier}
                onSuccess={(kws) => setRows((prev) => [...prev, ...kws])}
            />
            <KeywordResearchModal
                open={researchOpen}
                onClose={() => setResearchOpen(false)}
                projectId={project.id}
                existingKeywords={rows.map((r) => r.keyword)}
                limit={meta.limit}
                tier={meta.tier}
                onSuccess={(kws) => setRows((prev) => [...prev, ...kws])}
            />
        </div>
    );
}
